import {
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FC, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Card, Combo, IconButton, Option, Paragraph } from '@treams/treams-ui';

import { CloseIcon, ReorderIcon } from '../../icons';

type Props = {
	fetch?: () => void;
	placeholder?: string;
	options: Option[];
	selectedOptions?: Option[];
	onUpdate: (options: Option[]) => void;
};

const OrderCard = styled(Card)`
	> div {
		padding: 10px;
	}
`;

const SortableItem = (props: { item: Option; onDelete: (option: Option) => void }) => {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
		id: props.item.value!,
		animateLayoutChanges: () => false,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<Flex width="100%" mb={2} style={style}>
			<Flex ref={setNodeRef} {...attributes} {...listeners} alignItems="center" width={20}>
				<ReorderIcon />
			</Flex>
			<OrderCard ml={2} width="100%">
				<Flex justifyContent="space-between" alignItems="center">
					<Paragraph>{props.item.title}</Paragraph>
					<IconButton
						type="plain"
						icon={<CloseIcon />}
						small
						onClick={() => props.onDelete(props.item)}
					/>
				</Flex>
			</OrderCard>
		</Flex>
	);
};

export const OrderedMultiSelect: FC<Props> = ({
	fetch,
	placeholder,
	options,
	selectedOptions,
	onUpdate,
}) => {
	const [selected, setSelected] = useState<Option[]>(selectedOptions ?? []);
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (active.id !== over?.id) {
			setSelected((items) => {
				const oldIndex = items.findIndex((i) => i.value === active.id);
				const newIndex = items.findIndex((i) => i.value === over?.id);

				const updatedSelectedItems = arrayMove(items, oldIndex, newIndex);
				onUpdate(updatedSelectedItems);
				return updatedSelectedItems;
			});
		}
	};

	const updateSelected = (choice: { custom: string } | { value: string } | undefined) => {
		if (choice && 'value' in choice) {
			const option = options.find((o) => o.value === choice.value)!;
			const newState = [...selected, option];
			onUpdate(newState);
			setSelected(newState);
		}
	};

	const deleteItem = (selectedItem: Option) => {
		const newState = selected.filter((item) => item.value !== selectedItem.value);
		onUpdate(newState);
		setSelected(newState);
	};

	return (
		<Box>
			<Box mb={3}>
				<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
					<SortableContext
						items={selected.map((s) => s.value!)}
						strategy={verticalListSortingStrategy}
					>
						{selected.map((item, index) => (
							<SortableItem key={index} item={item} onDelete={(item) => deleteItem(item)} />
						))}
					</SortableContext>
				</DndContext>
			</Box>

			<Combo
				clearInputOnSelection
				placeholder={placeholder}
				custom={false}
				fetch={fetch}
				onChange={(choice) => updateSelected(choice)}
				options={options}
			/>
		</Box>
	);
};
